import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogTitle, InputLabel, MenuItem, Popper, Select, SelectChangeEvent, Slider, TextField } from '@mui/material';
import { useMediaDevices } from "react-media-devices";
import { Result, useZxing } from 'react-zxing';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { usePinch } from '@use-gesture/react';
import { Buffer } from "buffer";

import { AppConfig } from '../../config/appConfig';

import { IContainer } from '../../types/container';
import { ICustomerLocation } from '../../types/customer';

import { IGeneratedDocument } from '../../types/document';
import { ILoadMaterial } from '../../types/load';
import { IManifest } from '../../types/manifest';
import { IMaterialOption } from '../../types/list';
import { IMaterialInventory, IMaterialInventoryAssignment, IMaterialPrice } from '../../types/material';

import { ErrorProvider } from '../../classes/errorProvider';
import { useListContext } from '../../contexts/listContext';
import { AppSettingService } from '../../services/appSettingService';
import { LoadService } from '../../services/loadService';
import { MaterialPriceService } from '../../services/materialPriceService';
import { UtilityService } from '../../services/utilityService';

import Spinner from '../spinner';
import { Icons } from '../icons';
import NumberField from '../shared/numberField';
import WorkingModal, { IWorkingModalProps } from '../workingModal';
import loadPositionMap from '../../../src/assets/LoadPositionMap.jpg';
import scanBeep from '../../assets/beep.mp3';
import Json from '../json';

import '../../styles/loadMaterialsForm.scss';

export interface ILoadMaterialsFormProps {
	loadId?: string;
	customerId: number;
	vendorId: number;
	materials: ILoadMaterial[];
	materialPrices: IMaterialPrice[];
	manifests: IManifest[];
	isLoadingManifests: boolean;
	customerLocations: ICustomerLocation[];
	status: string;
	source: string;
	updateSource: (source: string) => void;
	updateLoadMaterials: (materials: ILoadMaterial[]) => void;
	onTabChangeRequest: (tab: string) => void;
}

const LoadMaterialsForm: React.FunctionComponent<ILoadMaterialsFormProps> = ({ loadId, customerId, materials, materialPrices, manifests, isLoadingManifests, customerLocations, status, source, updateSource, updateLoadMaterials, onTabChangeRequest }) => {
	const [dataView, setDataView] = useState<ILoadMaterial[]>([]);
	const listContext = useListContext();
	const [containerOptions, setContainerOptions] = useState<IContainer[]>([]);
	const [materialOptions, setMaterialOptions] = useState<IMaterialOption[]>([]);
	const [unassignedMaterialInventories, setUnassignedMaterialInventories] = useState<IMaterialInventory[]>([]);

	const positionOptions: string[] = [];
	for (let position = 1; position <= 30; position++) {
		positionOptions.push(`${position}A`);
		positionOptions.push(`${position}B`);
	}
	const [allPositions, setAllPositions] = useState<string[]>(positionOptions);
	const [positionErrors, setPositionErrors] = useState<undefined | string[]>([]);
	const [showAllErrors, setShowAllErrors] = useState(false);

	//Fields
	const [selectedMaterialId, setSelectedMaterialId] = useState<number>(0);
	const [isAutocompletingMaterials, setIsAutocompletingMaterials] = useState(false);
	const [isAutocompletingInventory, setIsAutocompletingInventory] = useState(false);
	const [autocompleteMaterialInputValue, setAutocompleteMaterialInputValue] = useState<string>("");
	const [autocompleteInventoryInputValue, setAutocompleteInventoryInputValue] = useState<string>("");
	const [selectedMaterialInventoryId, setSelectedMaterialInventoryId] = useState<number>(-1);
	const [selectedMaterialInventory, setSelectedMaterialInventory] = useState<IMaterialInventory | undefined>(undefined);
	const [materialWeight, setMaterialWeight] = useState<number>(0);
	const [materialContainerId, setMaterialContainerId] = useState<number>(0);
	const [isMaterialContainerLocked, setIsMaterialContainerLocked] = useState<boolean>(false);
	const [materialPosition, setMaterialPosition] = useState<string>(allPositions[0]);
	const [materialQuantity, setMaterialQuantity] = useState<number>(1);
	const [selectedCustomerLocationId, setSelectedCustomerLocationId] = useState<number>(-1);
	const [selectedManifestId, setSelectedManifestId] = useState<number>(0);
	const [totalMaterialWeight, setTotalMaterialWeight] = useState<number>(0);
	const [totalContainerWeight, setTotalContainerWeight] = useState<number>(0);

	const [isLoadingMaterialOptions, setIsLoadingMaterialOptions] = useState(true);
	const [isLoadingMaterialInventories, setIsLoadingMaterialInventories] = useState(true);
	const [isAdding, setIsAdding] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const [isRemovingId, setIsRemovingId] = useState<number | undefined>();
	const [isRefreshingLoadMaterials, setIsRefreshingLoadMaterials] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [statusMessage, setstatusMessage] = useState<string | undefined>();
	const [showForm, setShowForm] = useState(true);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [showNoManifestWarnings, setShowNoManifestWarnings] = useState<boolean | undefined>(undefined);

	const [showScanDialog, setShowScanDialog] = useState(false);
	const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
	const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState<string | undefined>(undefined);
	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const [allowTorchButton, setShowTorchButton] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(1);

	const [sortField, setSortField] = useState("material");
	const [sortAscending, setSortAscending] = useState(true);

	const { devices } = useMediaDevices({ constraints: { video: { facingMode: "environment", }, audio: false } });
	const preferredDeviceKeywords = ["back", "rear", "environment"];
	const avoidedDeviceKeywords = ["front", "user", "face", "selfie"];
	const topContainerOptionName = 'none';

	const [capabilities, setCapabilities] = useState(null);
	const videoRef = useRef(null);
	const { ref: zxingRef, torch } = useZxing({
		onDecodeResult(result) {
			handleQrScan(result);
		},
		onError(error) {
			handleCameraError(error);
		},
		paused: !showScanDialog,
		deviceId: selectedVideoDeviceId
	});
	if (zxingRef.current) {
		zxingRef.current.onplaying = () => {
			setIsVideoPlaying(true);
		};
	}

	// For sizing the autocomplete to the contents
	const autoCompleteMaterialRef = useRef(null);
	const autoCompleteInventoryRef = useRef(null);
	const CustomPopper = function (props) {
		return <Popper {...props} style={{ popper: { maxWidth: 'fit-content' } }} placement="bottom-start" />;
	};

	const audio = new Audio(scanBeep);
	const beep = async () => {
		await audio.play().catch((error) => {
			console.error("Error beeping:", error);
		});
	}

	//#region useEffects
	useEffect(() => {
		loadAppSettings();
		setContainerOptions(getCustomContainerOptions(listContext.containers));
		if (materialPrices) {
			loadMaterialOptions();
		}
		updateSource("manual");
	}, [])

	useEffect(() => {
		if (isAutocompletingMaterials) {
			setAutocompleteMaterialInputValue("");
			autoCompleteMaterialRef.current?.focus();
		}
	}, [isAutocompletingMaterials])

	useEffect(() => {
		if (isAutocompletingInventory) {
			setAutocompleteInventoryInputValue("");
			autoCompleteInventoryRef.current?.focus();
		}
	}, [isAutocompletingInventory])

	useEffect(() => {
		if (devices) {
			var filteredDevices = devices.filter(d => d.kind == "videoinput");
			setVideoDevices(filteredDevices);

			if (filteredDevices.length > 0) {
				const preferredDevices = filteredDevices.filter(device => preferredDeviceKeywords.some(keyword => device.label.toLowerCase().includes(keyword)));
				if (preferredDevices.length > 0) {
					setSelectedVideoDeviceId(preferredDevices[0].deviceId);
					return;
				}

				const unavoidedDevices = filteredDevices.filter(device => !avoidedDeviceKeywords.some(keyword => device.label.toLowerCase().includes(keyword)));
				if (unavoidedDevices.length > 0) {
					setSelectedVideoDeviceId(unavoidedDevices[0].deviceId);
					return;
				}

				// The user's device has dumb camera labels and we can't tell which is the rear camera. Just pick the first one
				setSelectedVideoDeviceId(filteredDevices[0].deviceId);
			}
		}
	}, [devices])

	useEffect(() => {
		if (status) {

			if (sortField.toLowerCase()) {
				switch (sortField) {
					case "ready": {
						setstatusMessage("This load is ready to ship. Please contact the transporter to schedule pickup.");
						break;
					}
					case "pendingvendorbol": {
						setstatusMessage("This load is awaiting a vendor Bill of Lading.");
						break;
					}
					case "scheduled": {
						setstatusMessage(undefined);
						break;
					}
					case "shipped": {
						setstatusMessage(undefined);
						break;
					}
					default: {
						setstatusMessage(undefined);
						break;
					}
				}
			}
		}
	}, [status])

	useEffect(() => {
		setMaterialContainerId(containerOptions[0]?.id ?? 0);
	}, [containerOptions])

	//Sort
	useEffect(() => {
		if (materials) {
			let newData = materials.slice();

			if (sortField) {
				switch (sortField) {
					case "material-weight": {
						// Rows are now formatted like 1A, 12B, etc. Need to split into level and position for proper sorting
						newData.sort((a, b) => {
							const rowParts1 = { weight: a.weight, unit: a.uomAbbreviation };
							const rowParts2 = { weight: b.weight, unit: b.uomAbbreviation };
							return UtilityService.compareTwoFields(rowParts1, rowParts2, 'unit', 'weight', "string", "number");
						});
						break;
					}
					case "tare-weight": {
						newData.sort((a, b) => {
							const tareWeightA = a.container.weight * (a.quantity ?? 1);
							const tareWeightB = b.container.weight * (b.quantity ?? 1);
							return UtilityService.compareNumbers(tareWeightA, tareWeightB);
						});
						break;
					}
					case "container": {
						newData.sort((a, b) => UtilityService.compareStrings(a.container.name, b.container.name));
						break;
					}
					case "row": {
						// Rows are now formatted like 1A, 12B, etc. Need to split into level and position for proper sorting
						newData.sort((a, b) => {
							const rowParts1 = { level: a.row.substring(a.row.length - 1), position: parseInt(a.row.substring(0, a.row.length - 1)) };
							const rowParts2 = { level: b.row.substring(b.row.length - 1), position: parseInt(b.row.substring(0, b.row.length - 1)) };
							return UtilityService.compareTwoFields(rowParts1, rowParts2, 'level', 'position', "string", "number");
						});
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.materialName, b.materialName));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
		}
	}, [materials, sortField, sortAscending])

	//Calculate weight
	useEffect(() => {
		if (materials) {
			let materialWeight = 0;
			let containerWeight = 0;
			materials.map((material) => {
				materialWeight += material.weight * material.lbsConversion;
				containerWeight += (material.materialPriceIsGrossWeightMaterial ? 0 : material.container.weight) * (material.quantity ?? 1);
			})

			setTotalMaterialWeight(materialWeight);
			setTotalContainerWeight(containerWeight);
		}
	}, [materials])

	useEffect(() => {
		handleMaterialSelection(source == "manual" ? selectedMaterialId.toString() : (selectedMaterialInventory?.materialId.toString() ?? "0"));
	}, [selectedMaterialId, selectedMaterialInventoryId])

	useEffect(() => {
		const isHazardous = isSelectedMaterialHazardous();
		const qualifyingManifests = manifests.filter(m => m.isHazardous || m.isHazardous == isHazardous);
		const selectedManifest = manifests.find(m => m.id == selectedManifestId);
		if (isHazardous === undefined || (!selectedManifest?.isHazardous && selectedManifest?.isHazardous !== isHazardous)) {
			setSelectedManifestId(0);
		}

		if (showNoManifestWarnings !== undefined) {
			setShowNoManifestWarnings(isHazardous !== undefined && qualifyingManifests.length == 0);
		}
	}, [source, selectedMaterialId, selectedMaterialInventoryId])

	//Check loading rules
	useEffect(() => {
		let errors: string[] = [];
		let positionWeightsA: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
		let positionWeightsB: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

		//Put all the gross, converted weights into arrays
		materials.map((material) => {
			let position = parseInt(material.row.replace(/\D/g, ''));
			if (material.row.includes('A')) {
				positionWeightsA[position - 1] = (material.weight * material.lbsConversion) + material.container.weight;
			}
			else {
				positionWeightsB[position - 1] = (material.weight * material.lbsConversion) + material.container.weight;
			}
		});

		//Rule One: The combined weight of 1-8A, and 1-8B can not exceed 12,000 pounds
		let ruleOneWeight = 0;
		for (let i = 0; i < 8; i++) {
			ruleOneWeight = ruleOneWeight + positionWeightsA[i] + positionWeightsB[i];
		}

		if (ruleOneWeight > 12000) {
			errors.push('The combined weight of positions 1-8, levels A and B, is over 12,000 pounds.');
		}

		//Rule Two: The combined weight of both levels A and B in any position can not exceed 2,000 pounds. For example 1A + 1B must not exceed 2,000.
		for (let i = 0; i < 30; i++) {
			if ((positionWeightsA[i] + positionWeightsB[i]) > 2000) {
				errors.push(`The combined weight of levels A and B in position ${i + 1} is over 2,000 pounds.`);
			}
		}

		//Rule Three: The combined weight of 23-30A, and 23-30B can not exceed 12,000 pounds
		let ruleThreeWeight = 0;
		for (let i = 22; i < 30; i++) {
			ruleThreeWeight = ruleThreeWeight + positionWeightsA[i] + positionWeightsB[i];
		}

		if (ruleThreeWeight > 12000) {
			errors.push('The combined weight of positions 23-30, levels A and B, is over 12,000 pounds.');
		}

		//Rule Four: The combined weight of the left side can not be more than 5% different than the combined weight of the right side.
		let leftWeight = 0;
		let rightWeight = 0;

		for (let i = 0; i < 30; i++) {
			if (i % 2 !== 0) {
				rightWeight = rightWeight + positionWeightsA[i] + positionWeightsB[i];
			}
			else {
				leftWeight = leftWeight + positionWeightsA[i] + positionWeightsB[i];
			}
		}

		if (leftWeight > (rightWeight * 1.05)) {
			errors.push(`The combined weight of all left side positions is ${leftWeight.toLocaleString()} which is more than 5% heavier than the right side positions.`);
		}
		if (rightWeight > (leftWeight * 1.05)) {
			errors.push(`The combined weight of all right side positions is ${rightWeight.toLocaleString()} which is more than 5% heavier than the left side positions.`);
		}

		//Rule Five: Within a position, the level B position can not exceed the weight of the level A position. So 1B can not weight more than 1A.
		for (let i = 0; i < 30; i++) {
			if (positionWeightsA[i] < positionWeightsB[i]) {
				errors.push(`The B level material in position ${i + 1} is heavier than the A level material. Within any position, the heavier material must be on the bottom level.`);
			}
		}

		//Rule Six: The combined weight of all positions in both levels can not exceed 42,000 pounds.
		let totalWeight = 0;

		for (let i = 0; i < 30; i++) {
			totalWeight = totalWeight + positionWeightsA[i] + positionWeightsB[i];
		}
		if (totalWeight > 42000) {
			errors.push(`The total weight is ${totalWeight.toLocaleString()}. It should not exceed 42,000 pounds.`);
		}

		// DC 06/25/2026: Removing this check as customers now have their own custom defined minimum weights
		//Rule Seven: The combined weight of all positions in both levels can not be less than 38,000 pounds.
		// if (totalWeight < 38000) {
		// 	errors.push(`The total weight is ${totalWeight.toLocaleString()}. It should not be less than 38,000 pounds.`);
		// }

		if (errors.length > 0) {
			setPositionErrors(errors.slice());
		}

	}, [materials])

	useEffect(() => {
		if (materialPrices) {
			loadMaterialOptions();
		}
	}, [materialPrices]);

	useEffect(() => {
		resetMaterialOptions();
	}, [source]);

	useEffect(() => {
		if (!isLoadingMaterialOptions) {
			resetMaterialOptions();
		}
	}, [isLoadingMaterialOptions]);

	useEffect(() => {
		if (customerId) {
			loadMaterialInventories();
		}
	}, [customerId]);

	useEffect(() => {
		var materialInventory = unassignedMaterialInventories.find((item) => item.id == selectedMaterialInventoryId);
		setSelectedMaterialInventory(materialInventory);
	}, [selectedMaterialInventoryId])

	useEffect(() => {
		setIsVideoPlaying(false);
	}, [showScanDialog, selectedVideoDeviceId])

	useEffect(() => {
		if (isRefreshingLoadMaterials) {
			// The parent component successfully updated the load materials
			setIsRefreshingLoadMaterials(false);
		}
	}, [materials])
	//#endregion useEffects

	//#region Loading methods
	const loadAppSettings = async () => {
		// Low priority, no loading statuses necessary	
		const torchSetting = (await AppSettingService.getAppSettingByName("AllowQrScannerTorch"))?.data;
		setShowTorchButton(AppSettingService.mapAppSetting(torchSetting).valueAsBoolean());
	}

	const loadMaterialInventories = () => {
		setIsLoadingMaterialInventories(true);
		LoadService.getUnassignedMaterialInventoriesByCustomerId(customerId)
			.then((response) => {
				console.log('LoadService.getUnassignedMaterialInventoriesByCustomerId: SUCCESS', response);
				const inventories = LoadService
					.mapMaterialInventories(response.data).sort((a, b) => UtilityService.compareNumbers(a.id, b.id));

				// Differetiate between price by weight or container if there are material prices with the same material name
				const distinctInventorMaterialIds = Array.from(new Set(inventories.map(m => m.materialId)));
				for (let materialId of distinctInventorMaterialIds) {
					const optionsForMaterial = inventories.filter(o => o.materialId == materialId);
					if (optionsForMaterial.length > 1) {
						for (let material of optionsForMaterial) {
							material.materialName = material.materialName + ` (by ${typeof material.quantity === "number" ? "container" : "weight"})`;
						}
					}
				}

				setUnassignedMaterialInventories(inventories);
			})
			.catch((error) => {
				console.log('LoadService.getUnassignedMaterialInventoriesByCustomerId: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsLoadingMaterialInventories(false);
			});
	}

	const loadMaterialOptions = async () => {
		var options = MaterialPriceService.mapMaterialPricesToMaterialOptions(materialPrices);

		// Add the container name to each material that is priced by container
		for (let material of options.filter(o => o.priceByContainer == true)) {
			material.name = material.name + ` (by ${getContainerById(material.containerId)?.name ?? "container"})`;
		}

		// Differetiate between units of measure if there are material prices with the same material name priced by weight
		for (let materialId of Array.from(new Set(options.map(m => m.tilsCode)))) {
			const optionsForMaterial = options.filter(o => o.tilsCode == materialId.toString() && o.priceByContainer != true);
			if (optionsForMaterial.length > 1) {
				for (let material of optionsForMaterial) {
					material.name = material.name + ` (by ${material.uom})`;
				}
			}
		}

		setMaterialOptions(options.sort((a, b) => a.name > b.name ? 1 : -1));
		setIsLoadingMaterialOptions(false);
	}

	const refreshLoadMaterials = () => {
		setIsRefreshingLoadMaterials(true);
		LoadService.getLoad(loadId)
			.then((response) => {
				console.log('LoadService.getLoad: SUCCESS', response);
				const load = LoadService.mapLoad(response.data);
				updateLoadMaterials(load.materials);
			})
			.catch((error) => {
				console.log('LoadService.getLoad: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
				setIsRefreshingLoadMaterials(false);
			});
	}
	//#endregion Loading methods

	//#region "get" utility methods
	const getContainerById = (id: number) => {
		return listContext.containers.find(c => c.id == id);
	}

	const getCustomContainerOptions = (options: IContainer[]): IContainer[] => {
		if (options && options.length > 0) {
			const topOption = options.find(c => c.name.toLowerCase() == topContainerOptionName.toLowerCase());
			if (topOption) {
				return [topOption, ...options.filter(c => c.id != topOption.id)];
			}
		}
		return options;
	}

	const getGrossWeight = (material: ILoadMaterial) => {
		let grossWeight = 0;

		if (material.weight) {
			grossWeight = grossWeight + material.weight;
		}

		if (material.container.weight) {
			let convertedContainerWeight = material.container.weight / (material.lbsConversion > 0 ? material.lbsConversion : 1);
			grossWeight = grossWeight + (convertedContainerWeight * (material.quantity ?? 1));
		}

		return grossWeight;
	};

	const getMaterialInventoryById = (id: number) => {
		return unassignedMaterialInventories.find(m => m.id == id);
	}

	const getSelectedContainer = () => {
		return containerOptions.find(c => c.id === materialContainerId);
	}

	const getSelectedMaterial = () => {
		return materialOptions.find(m => +m.value === selectedMaterialId);
	}

	const getSelectedMaterialInventory = () => {
		return unassignedMaterialInventories.find(mi => mi.id === selectedMaterialInventoryId);
	}

	const getSelectedCustomerLocation = () => {
		return customerLocations.find(cl => cl.id === selectedCustomerLocationId);
	}

	const isSelectedMaterialHazardous = (): boolean | undefined => {
		if (source == "manual") {
			const material = getSelectedMaterial();
			return material ? material.isHazardous : undefined;
		}
		else if (source == "inventory") {
			const materialInventory = getSelectedMaterialInventory();
			return materialInventory ? materialInventory.isHazardous : undefined;
		}

		return undefined;
	}
	//#endregion "get" utility methods

	//#region Event handlers
	const handleCameraError = (error: any) => {
		setShowScanDialog(false);
		if (error?.toString()?.toLowerCase()?.includes("permission denied")) {
			setErrorMessage("Your browser has been denied access to the camera. Please allow camera access and try again.");
		}
		else {
			setErrorMessage("An error occurred while trying to access the camera: " + error);
		}
	}

	const handleContainerSelection = (event: SelectChangeEvent<number>) => {
		setMaterialContainerId(parseInt(event.target.value.toString()));
	}

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handleMaterialSelection = (materialId: string) => {
		// If the material price selection is priced by container, select and lock the container 
		// field, otherise select the top container in the list
		const containerWasLocked = isMaterialContainerLocked;
		setIsMaterialContainerLocked(false);
		setSelectedMaterialId(parseInt(materialId));
		const matches = materialOptions.filter((item) => item.value == materialId);
		if (matches?.length > 0) {
			const materialPrice = matches[0];

			if (materialPrice.priceByContainer && materialPrice.containerId) {
				setIsMaterialContainerLocked(true);
				const container = containerOptions.find(c => c.id == materialPrice.containerId);
				setMaterialContainerId(container ? container.id : 0);
			}
			else if (containerWasLocked) {
				setMaterialContainerId(containerOptions[0]?.id ?? 0);
			}
		}
	}

	const handleOpenScanDialog = () => {
		setErrorMessage(undefined);
		setShowScanDialog(true);
	}

	const handlePositionUpdate = (event: SelectChangeEvent<string>) => {
		setMaterialPosition(event.target.value);
	};

	const handleQrScan = async (scan: Result) => {
		await beep();
		setShowScanDialog(false);
		setErrorMessage(undefined);
		setSelectedMaterialInventoryId(-1);

		let scanJson;
		try {
			const scanText = scan.getText()
				.replaceAll("\x13", "-")  // Replace EN dash with hyphen (QR scanner misreads this as DC3);
				.replaceAll("\x14", "-"); // Replace EM dash with hyphen (QR scanner misreads this as DC4);
			scanJson = JSON.parse(scanText);
		}
		catch (error) {
			setErrorMessage("The scanned QR code does not describe a Material Inventory item.");
			return;
		}

		if (!scanJson.hasOwnProperty("id") || !scanJson.hasOwnProperty("customerLocationId") || !scanJson.hasOwnProperty("remoteShipmentId")) {
			setErrorMessage("The scanned QR code does not describe a Material Inventory item.");
			return;
		}

		const scannedMaterialInventoryId = parseInt(scanJson.id);
		if (Number.isNaN(scannedMaterialInventoryId) || scannedMaterialInventoryId < 1) {
			setErrorMessage("The scanned QR code does not contain a valid Material Inventory ID.");
			return;
		}

		var matchedItem = unassignedMaterialInventories.find((item) => item.id == scannedMaterialInventoryId);
		if (!matchedItem) {
			setErrorMessage(`The scanned QR code (ID ${scannedMaterialInventoryId}) does not match any Material Inventory items in the list.`);
			return;
		}

		setSelectedMaterialInventoryId(matchedItem.id);
	}

	const handleRemove = async (loadMaterialRecordId: number | undefined) => {
		setErrorMessage(undefined);
		setIsRemoving(true);
		setIsRemovingId(loadMaterialRecordId);

		if (loadId && loadMaterialRecordId) {
			LoadService.removeMaterial(loadId, loadMaterialRecordId)
				.then((response) => {
					console.log('LoadService.removeMaterial: SUCCESS', response);
					refreshLoadMaterials();

					if (source == "inventory") {
						// If removing an inventory item, reload the inventory list so the removed item shows back up
						loadMaterialInventories();
					}
				})
				.catch((error) => {
					console.log('LoadService.removeMaterial: ERROR', error);
					setErrorMessage("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsRemoving(false);
					setIsRemovingId(undefined);
				});
		}
	}

	const handleSubmitAdd = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (isAutocompletingMaterials || isAutocompletingInventory) return;
		setErrorMessage(undefined);

		if (source == "manual") {
			if (loadId && selectedMaterialId && materialWeight >= 0) {
				addMaterialManually();
			}
		}
		else {
			addMaterialInventory();
		}
	}
	//#endregion Event handlers

	//#region Action methods
	const addMaterialInventory = () => {
		if (selectedMaterialInventoryId < 1) {
			setErrorMessage("A Material Inventory item must be selected.");
			return;
		}
		setIsAdding(true);

		const newAssignment: IMaterialInventoryAssignment = {
			materialInventoryId: selectedMaterialInventoryId,
			position: materialPosition,
			loadId: +loadId
		};
		LoadService.addMaterialInventoryToLoad(newAssignment)
			.then((response) => {
				console.log('LoadService.addMaterialInventoryToLoad: SUCCESS', response);
				refreshLoadMaterials();
				setSelectedMaterialInventoryId(-1);
				setUnassignedMaterialInventories(unassignedMaterialInventories.filter(m => m.id != selectedMaterialInventoryId));
			})
			.catch((error) => {
				console.log('LoadService.addMaterialInventoryToLoad: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsAdding(false);
			});
	}

	const addMaterialManually = () => {
		if (loadId && selectedMaterialId && materialWeight >= 0) {
			setIsAdding(true);

			const newMaterial: ILoadMaterial = {
				loadId: parseInt(loadId),
				materialId: selectedMaterialId,
				row: materialPosition,
				weight: materialWeight,
				containerId: materialContainerId,
				quantity: (getSelectedContainer()?.fillsPosition === false && getSelectedMaterial()?.priceByContainer == true) ? materialQuantity : 1,
				materialInventoryLocation: selectedCustomerLocationId > 0 ? getSelectedCustomerLocation()?.locationName ?? null : null,
				manifestId: selectedManifestId || null
			}
			LoadService.addMaterial(parseInt(loadId), newMaterial)
				.then((response) => {
					console.log('LoadService.addMaterial: SUCCESS', response);
					refreshLoadMaterials();
				})
				.catch((error) => {
					console.log('LoadService.addMaterial: ERROR', error);
					setErrorMessage("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsAdding(false);
				});
		}
	}

	const capitalizeFirstLetter = (text: string) => {
		return text?.split(" ").map(word => word.substring(0, 1).toUpperCase() + word.substring(1).toLowerCase()).join(" ") ?? "";
	}

	const resetMaterialOptions = () => {
		setMaterialWeight(source == "manual" ? 0 : -1);
		if (source == "manual") {
			setSelectedMaterialId(materialOptions.length > 0 ? parseInt(materialOptions[0].value) : 0);
		} else {
			setSelectedMaterialInventoryId(-1);
		}
	}

	const handleExportToExcel = () => {
		setErrorMessage(undefined);
		setWorkingModalParams({ params: { content: "Exporting load material list..." } });


		LoadService.exportLoadMaterialListToExcel(+loadId)
			.then((response) => {
				console.log('LoadService.exportLoadMaterialListToExcel: SUCCESS', response);
				downloadFromByteArray(response.data);
			})
			.catch((error) => {
				console.error('LoadService.exportLoadMaterialListToExcel: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	};

	const downloadFromByteArray = (file: IGeneratedDocument): void => {
		const uint8ArrayData = new Uint8Array(Buffer.from(file.contents, 'base64'));
		const blob = new Blob([uint8ArrayData], { type: file.contentType });
		const url = window.URL.createObjectURL(blob);

		// Create a hidden anchor element to trigger the download
		const link = document.createElement('a');
		link.href = url;
		link.download = file.name;
		link.style.visibility = 'hidden';
		document.body.appendChild(link);

		// Simulate a click to start the download
		link.click();

		// Clean up the DOM and revoke the object URL (after a slight delay)
		document.body.removeChild(link);
		// A delay is often necessary to ensure the browser has initiated the download
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 100);
	}
	//#endregion Action methods

	//#region Zoom/Pinch methods

	// Helper function to merge multiple refs (for zooming)
	function mergeRefs(...refs) {
		return (videoElement) => {
			for (const ref of refs) {
				if (typeof ref === 'function') {
					ref(videoElement); // Handle callback refs
				} else if (ref && 'current' in ref) {
					ref.current = videoElement; // Handle object refs
				}
			}
		};
	}

	const mergedRef = useCallback(
		(videoElement) => {
			// Pass the video element to both refs
			mergeRefs(videoRef, zxingRef)(videoElement);

			if (videoElement && isVideoPlaying) {
				const mediaStream = videoElement.srcObject;
				if (mediaStream) {
					const [videoTrack] = mediaStream.getVideoTracks();
					const trackCapabilities = videoTrack.getCapabilities();
					if ('zoom' in trackCapabilities) {
						setCapabilities(trackCapabilities);
						videoTrack.applyConstraints({ advanced: [{ zoom: zoomLevel }] });
					}
				}
			}
		},
		[zxingRef, isVideoPlaying]
	);

	const handleZoomChange = (event, newZoomLevel: number) => {
		setZoomLevel(newZoomLevel);
		if (videoRef.current && capabilities) {
			const [videoTrack] = videoRef.current.srcObject.getVideoTracks();
			if (newZoomLevel >= capabilities.zoom.min && newZoomLevel <= capabilities.zoom.max) {
				videoTrack.applyConstraints({ advanced: [{ zoom: newZoomLevel }] });
				setZoomLevel(newZoomLevel);
			}
		}
	};

	const bindPincher = usePinch(
		({ active, movement: [mx, my], offset: [ox, oy], memo }) => {
			// If the gesture is active, update the scale and position
			if (active) {

				// Apply zoom to camera if capabilities are available
				if (videoRef.current && capabilities?.zoom) {
					const newZoomLevel = Math.max(
						capabilities.zoom.min,
						Math.min(capabilities.zoom.max, ox)
					);

					const [videoTrack] = videoRef.current.srcObject.getVideoTracks();
					videoTrack.applyConstraints({ advanced: [{ zoom: newZoomLevel }] });
					setZoomLevel(newZoomLevel);
				}
			}
			return memo; // Return memo for consistent behavior across active states
		},
		{
			scaleBounds: { min: capabilities?.zoom?.min, max: capabilities?.zoom?.max },
			preventDefault: true,
			eventOptions: { passive: false }
		}
	);
	//#endregion Zoom/Pinch methods

	return (
		<section className="hwi-load-materials-form">
			<Spinner dependency={(isLoadingMaterialOptions || isLoadingManifests) && status.toLowerCase() == "loading"} text='Loading materials...'>
				{errorMessage &&
					<Alert className="alert-error" severity="error">{errorMessage}</Alert>
				}
				<>
					{status.toLowerCase() != "loading" ? (
						<Alert className="alert-warning" severity="warning" >
							This load is in <strong>{status.toLowerCase() == "pendingvendorbol" ? "Pending Vendor BOL" : status}</strong> status. It can no longer be updated.
						</Alert>
					) : (
						<>
							<Form onSubmit={(event) => handleSubmitAdd(event)}>
								<div className="row">
									<div className="header">
										<div className="form-title">Add Material</div>
										<span onClick={() => setShowForm(!showForm)}>
											{showForm &&
												<Icons.Up fill="#272f50" height="32px"></Icons.Up>
											}
											{!showForm &&
												<Icons.Down fill="#272f50" height="32px"></Icons.Down>
											}
										</span>
									</div>
									{showForm &&
										<>
											<div className="source">
												<InputLabel>Source</InputLabel>
												<Select
													name="source"
													value={source}
													required
													fullWidth
													onChange={(event) => { updateSource(event.target.value); }}
												>
													<MenuItem value="manual">Manual</MenuItem>
													<MenuItem value="inventory">From Inventory</MenuItem>
												</Select>
											</div>
											<div className="material">
												{source == "manual" ? (
													<>
														<InputLabel>Material</InputLabel>
														{!isAutocompletingMaterials ? (
															<Select
																name="material"
																className="material-field"
																value={selectedMaterialId || ''}
																required
																fullWidth
																inputProps={{ readOnly: true }}
																disabled={isLoadingMaterialOptions}
																onClick={() => { setIsAutocompletingMaterials(true); }}
															>
																{materialOptions.map((item, index) => (
																	<MenuItem value={item.value} key={index} ><span style={{ fontWeight: 'bold', marginRight: '5px' }}>{item.tilsCode?.toString().padStart(4, "0")}</span><span>{item.name}</span></MenuItem>
																))}
															</Select>
														) : (
															<Autocomplete
																openOnFocus={true}
																blurOnSelect
																disablePortal
																disableClearable
																fullWidth
																inputValue={autocompleteMaterialInputValue}
																PopperComponent={CustomPopper}
																onInputChange={(event, newInputValue) => { setAutocompleteMaterialInputValue(newInputValue); }}
																options={[...materialOptions].map(m => ({ ...m, name: `${m.tilsCode.toString().padStart(4, "0")} ${m.name}` }))}
																renderInput={(params) => <TextField {...params} inputRef={autoCompleteMaterialRef} />}
																renderOption={(props, item: IMaterialOption, { inputValue }) => {
																	const { key, ...optionProps } = props;
																	const matches = match(item.name, inputValue, { insideWords: true });
																	const parts = parse(item.name, matches);
																	return (
																		<MenuItem value={item.value} key={item.value} {...optionProps} >
																			<span>
																				{parts.map((part, index) => (
																					<span
																						key={index}
																						style={{
																							fontWeight: part.highlight ? 700 : 400,
																						}}
																					>
																						{part.text}
																					</span>
																				))}
																			</span>
																		</MenuItem>
																	);
																}}
																value={getSelectedMaterial() ?? ''}
																onChange={(event, item: IMaterialOption) => {
																	setSelectedMaterialId(+item.value)
																}}
																onClose={() => { setIsAutocompletingMaterials(false); }}
																getOptionLabel={(item) => (typeof item === 'object' ? `${item.tilsCode} ${item.name}` : '')}
																isOptionEqualToValue={(item, value) => { return JSON.stringify(item) === JSON.stringify(value) }}
															/>
														)}
													</>
												) : (
													<>
														<InputLabel>Material Inventory ID {(isLoadingMaterialInventories ? "(Loading inventories...)" : "")}</InputLabel>
														{!isAutocompletingInventory ? (
															<div className="material-inventory-container">
																<Select
																	name="material-inventory-id"
																	className="id-select"
																	value={selectedMaterialInventoryId || ''}
																	required
																	fullWidth
																	inputProps={{ readOnly: true }}
																	disabled={isLoadingMaterialInventories}
																	onClick={() => { setIsAutocompletingInventory(true); }}
																>
																	<MenuItem value={-1}>Select an inventory item or scan a QR code</MenuItem>
																	{unassignedMaterialInventories.map((item, index) => (
																		<MenuItem value={item.id} key={index} ><span style={{ fontWeight: 'bold', marginRight: '5px' }}>{item.id?.toString().padStart(6, "0")}</span><span>{item.materialName}</span></MenuItem>
																	))}
																</Select>
																<Button variant={"text"} className="scan-button" onClick={() => handleOpenScanDialog()} disabled={isLoadingMaterialInventories}>
																	<Icons.QrCode fill="#272f50" height="27px"></Icons.QrCode>
																</Button>
															</div>
														) : (
															<Autocomplete
																openOnFocus={true}
																blurOnSelect
																disablePortal
																disableClearable
																fullWidth
																inputValue={autocompleteInventoryInputValue}
																PopperComponent={CustomPopper}
																onInputChange={(event, newInputValue) => { setAutocompleteInventoryInputValue(newInputValue); }}
																options={[...unassignedMaterialInventories].map(i => ({ ...i, materialName: `${i.id.toString().padStart(6, "0")} ${i.materialName}` }))}
																renderInput={(params) => <TextField {...params} inputRef={autoCompleteInventoryRef} />}
																renderOption={(props, item: IMaterialInventory, { inputValue }) => {
																	const { key, ...optionProps } = props;
																	const matches = match(item.materialName, inputValue, { insideWords: true });
																	const parts = parse(item.materialName, matches);
																	return (
																		<MenuItem value={item.id} key={key} {...optionProps} >
																			<span>
																				{parts.map((part, index) => (
																					<span
																						key={index}
																						style={{
																							fontWeight: part.highlight ? 700 : 400,
																						}}
																					>
																						{part.text}
																					</span>
																				))}
																			</span>
																		</MenuItem>
																	);
																}}
																value={getSelectedMaterialInventory() ?? ''}
																onChange={(event, item: IMaterialInventory) => {
																	setSelectedMaterialInventoryId(item.id)
																}}
																onClose={() => { setIsAutocompletingInventory(false); }}
																getOptionLabel={(item) => (typeof item === 'object' ? `${item.id} ${item.materialName}` : '')}
																isOptionEqualToValue={(item, value) => { return JSON.stringify(item) === JSON.stringify(value) }}
															/>
														)}
													</>
												)}
											</div>
											<div className="weight">
												<InputLabel>{capitalizeFirstLetter((source == "manual" ? getSelectedMaterial()?.uom : selectedMaterialInventory?.uomName) || "Weight")}</InputLabel>
												<NumberField className="weight-field"
													name="weight"
													value={(source == "manual") ? (materialWeight > -1 ? materialWeight : "") : (selectedMaterialInventory?.weight ?? "")}													
													min={0}
													required
													fullWidth
													type="number"
													disabled={source == "inventory"}
													onChange={(event) => { setMaterialWeight(parseFloat(event.target.value)) }}
													onFocus={(event) => event.target.select()}
													precision={AppConfig.precisionDecimalsMaximum}
												></NumberField>
											</div>
											<div className="container">
												<InputLabel>Container</InputLabel>
												<Select
													className="container-field"
													value={(source == "manual") ? (materialContainerId > 0 ? materialContainerId : "") : (selectedMaterialInventory?.containerId ?? "")}
													required
													fullWidth
													disabled={source == "inventory" || isMaterialContainerLocked}
													onChange={handleContainerSelection}
												>
													{containerOptions.map((item, index) => (
														<MenuItem value={item.id} key={index} >{item.name}</MenuItem>
													))}
												</Select>
											</div>
											{((source === "manual" && getSelectedContainer()?.fillsPosition === false && getSelectedMaterial()?.priceByContainer == true) ||
												(source === "inventory" && typeof selectedMaterialInventory?.quantity === "number")) &&
												<div className='quantity'>
													<InputLabel>Quantity *</InputLabel>
													<NumberField
														name="quantity"
														value={(source == "manual") ? (materialQuantity > -1 ? materialQuantity : "") : (selectedMaterialInventory?.quantity ?? "")}
														min={0}
														step={1}
														required
														fullWidth
														disabled={source == "inventory"}
														onChange={(event) => { setMaterialQuantity(parseFloat(event.target.value)) }}
														onFocus={(event) => event.target.select()}
													></NumberField>
												</div>
											}
											<div className="customer-location">
												<InputLabel>Customer Location</InputLabel>
												<Select className="customer-location-field"
													name="customer-location"
													value={(source == "manual") ? (selectedCustomerLocationId) : (selectedMaterialInventory?.customerLocationId ?? -1)}//{selectedCustomerLocationId}
													required
													fullWidth
													onChange={(event) => { setSelectedCustomerLocationId(event.target.value as number); }}
													disabled={source == "inventory"}
												>
													<MenuItem value={-1} key={-1}>None</MenuItem>
													{customerLocations.map((item, index) => (
														<MenuItem value={item.id} key={index}>{item.locationName}</MenuItem>
													))}
												</Select>
											</div>
											<div className="row-position">
												<InputLabel>Position</InputLabel>
												<Select className="row-position-field"
													name="position"
													value={materialPosition}
													required
													fullWidth
													onChange={(event) => { handlePositionUpdate(event); }}
												>
													{allPositions.map((item, index) => (
														<MenuItem value={item} key={index}>{item}</MenuItem>
													))}
												</Select>
											</div>
											<div className="manifest">
												<InputLabel>Manifest</InputLabel>
												<Select
													className="manifest-field"
													value={selectedManifestId}
													fullWidth
													onChange={(event) => { setSelectedManifestId(+event.target.value) }}
													onClick={() => { setShowNoManifestWarnings(manifests.filter(m => m.isHazardous || m.isHazardous == isSelectedMaterialHazardous()).length == 0 ? true : showNoManifestWarnings) }}
													readOnly={manifests.filter(m => m.isHazardous || m.isHazardous == isSelectedMaterialHazardous()).length == 0}
												>
													<MenuItem value={0}>None</MenuItem>
													{manifests?.filter(m => m.isHazardous || m.isHazardous === isSelectedMaterialHazardous()).map((item, index) => (
														<MenuItem value={item.id} key={index} >{item.number}</MenuItem>
													))}
												</Select>
											</div>
											<div>
												{showNoManifestWarnings && isSelectedMaterialHazardous() !== undefined &&
													<div className="no-manifest-warning">
														This shipment has no {isSelectedMaterialHazardous() && <strong>hazardous </strong>}manifests. You can add one on the <a href="#" onClick={() => { onTabChangeRequest?.("manifests"); }}><Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem> Manifests tab</a>.
													</div>
												}
											</div>
											<div className="controls">
												<Button variant="contained" type="submit" className="save-button" disabled={isAdding} >{isAdding ? "Adding..." : "Add Material"}</Button>
											</div>
										</>
									}

								</div>
							</Form>
							<div className="warnings">
								<Alert className="alert-warning-main"
									severity="warning"
									action={
										<Button variant="text" color='warning' onClick={() => setShowAllErrors(!showAllErrors)}>{showAllErrors ? "Hide List" : "Show List"}</Button>
									}
								>
									This shipment has a total of <strong>{positionErrors.length} warnings.</strong>
								</Alert>
								{showAllErrors &&
									<>
										{positionErrors.map((item, index) => (
											<Alert className="alert-warning"
												severity="warning"
												key={index}
											>
												{item}
											</Alert>
										))}
									</>
								}
							</div>
						</>
					)}
					<div className="material-table-controls">
						<Button variant="contained" onClick={() => handleExportToExcel()} disabled={isRefreshingLoadMaterials || (dataView?.length ?? 0) === 0}>Export to Excel</Button>
					</div>
					<div className="material-table">
						<div className="headers">
							<div className="col-material-inventory-id">
								<p onClick={() => handleHeaderClick("inventory")}>
									Inventory
									{" "}
									{(sortField == "inventory" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "inventory" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-material">
								<p onClick={() => handleHeaderClick("material")}>
									Material
									{" "}
									{(sortField == "material" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "material" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-material-weight">
								<p onClick={() => handleHeaderClick("material-weight")}>
									Material WT
									{" "}
									{(sortField == "material-weight" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "material-weight" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-tare-weight">
								<p onClick={() => handleHeaderClick("tare-weight")}>
									Tare WT
									{" "}
									{(sortField == "tare-weight" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "tare-weight" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-container">
								<p onClick={() => handleHeaderClick("container")}>
									Container
									{" "}
									{(sortField == "container" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "container" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-row">
								<p onClick={() => handleHeaderClick("row")}>
									Position
									{" "}
									{(sortField == "row" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "row" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-location">
								<p onClick={() => handleHeaderClick("location")}>
									Location
									{" "}
									{(sortField == "location" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "location" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-controls"></div>
						</div>
						<Spinner dependency={isRefreshingLoadMaterials} text='Refreshing materials...'>
							<div className="body">
								{materials && materials?.length > 0 ? (
									<>
										{dataView.map((item, index) => (
											<div className="row" key={index}>
												<div className="col-material-inventory-id">
													{item.materialInventoryId ? (
														<a href={`/materialinventory/${item.materialInventoryId}`} target="_blank" rel="noopener noreferrer">
															<Icons.ViewItem height="16px"></Icons.ViewItem>
															{item.materialInventoryId}
														</a>
													) : (
														<>-</>
													)}
												</div>
												<div className="col-material">{item.materialName}</div>
												<div className="col-material-weight">{item.weight?.toLocaleString() ?? 0} {item.uomAbbreviation ?? ""}</div>
												<div className="col-tare-weight">
													{item.materialPriceIsGrossWeightMaterial ? (
														<>*</>
													) : (
														<>{((item.container?.weight ?? 0) * (item.quantity ?? 1))?.toLocaleString() ?? 0} lbs</>
													)}
													
												</div> {/* Tare weight is always in pounds */}
												<div className={`col-container ${item.container.name == "None" ? "inactive" : ""}`}>{item.container.name ? item.container.name.toLocaleString() : ""}{item.quantity > 1 ? ` x ${item.quantity}` : ""}</div>
												<div className="col-row">{item.row}</div>
												<div className={`col-location ${!item.materialInventoryLocation ? "inactive" : ""}`}>{item.materialInventoryLocation ?? "None"}</div>
												<div className="col-controls">
													{status.toLowerCase() == "loading" &&
														<Button onClick={() => handleRemove(item.id)} disabled={isRemoving}>{isRemoving && isRemovingId == item.id ? "Removing..." : (<><Icons.RemoveItem fill="#a82525" height="16px"></Icons.RemoveItem><span>&nbsp;Remove</span></>)}</Button>
													}
												</div>
											</div>
										))}
									</>
								) : (
									<div className="no-results">0 results</div>
								)}
							</div>
							<div className="footers">
								<div className="col-material-inventory-id"></div>
								<div className="col-material">
									<p onClick={() => handleHeaderClick("material")}>
									</p>
								</div>
								<div className="col-material-weight">
									<p>
										{totalMaterialWeight.toLocaleString()}{" lbs"}
									</p>
								</div>
								<div className="col-tare-weight">
									<p>
										{totalContainerWeight.toLocaleString()}{" lbs"}
									</p>
								</div>
								<div className="col-container">
									<p onClick={() => handleHeaderClick("container")}>
										{/* {totalContainerWeight.toLocaleString()}{" pounds"} */}
									</p>
								</div>
								<div className="col-row">
									<p onClick={() => handleHeaderClick("row")}>
									</p>
								</div>
								<div className="col-location">
									<p onClick={() => handleHeaderClick("location")}>
									</p>
								</div>

								<div className="col-controls"></div>
							</div>
						</Spinner>
					</div>
					{materials?.some(m => m.materialPriceIsGrossWeightMaterial) &&
						<div className="gross-weight-materials-warning">
							<p>{AppConfig.grossWeightMaterialMessage}</p>
						</div>
					}
					<div className="load-position-map">
						<img src={loadPositionMap} alt="Image" />
					</div>
				</>
				{/* <Json data={materialContainerId}></Json> */}
			</Spinner>
			<Dialog
				fullScreen={false}
				open={showScanDialog}
				className='hwi-material-inventory-qr-scanner-dialog'
				maxWidth='sm'
				keepMounted={true}
				fullWidth={true}>
				<DialogTitle id="title">
					<Spinner dependency={!isVideoPlaying} text='Starting camera...'>
						{"Scan QR Code"}
					</Spinner>
				</DialogTitle>
				{/* The video element can't be in the spinner or it won't be in the DOM and will never start playing */}
				<div className="video-container">
					<video {...bindPincher()} className={isVideoPlaying ? '' : 'hidden'} ref={mergedRef} />
				</div>
				<DialogActions>
					{capabilities?.zoom && (
						<div className="zoom">
							<div className="zoom-level">
								<span className="header">Zoom</span><br />
								<span className="data">
									x<span className="value">{zoomLevel.toFixed(1)}</span>
								</span>
							</div>
							<Slider value={zoomLevel} onChange={handleZoomChange} min={capabilities.zoom.min} max={capabilities.zoom.max} step={capabilities.zoom.step || 0.1} />
						</div>
					)}
					<div className="other">
						{videoDevices?.length > 1 &&
							<Select
								value={selectedVideoDeviceId ?? ""}
								onChange={(event) => setSelectedVideoDeviceId(event.target.value)}
								className='camera-select'
							>
								{videoDevices?.map(device => (
									<MenuItem key={device.deviceId} value={device.deviceId}>
										{device.label}
									</MenuItem>
								))}
							</Select>
						}
						{allowTorchButton && torch?.isAvailable &&
							<Button
								className={"torch" + (torch?.isOn ? " on" : "")}
								onClick={async () => { if (torch?.isOn) { await torch?.off(); } else { await torch?.on(); } }}
							>
								*
							</Button>
						}
						<Button className='cancel' color="error" variant='contained' onClick={() => { setShowScanDialog(false); }}>
							Cancel
						</Button>
					</div>
				</DialogActions>
			</Dialog>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</section>
	)
}

export default LoadMaterialsForm;